import { NavigateOptions } from "react-router-dom";

import { useAuthorizationStore } from "../../../store/authorizationStore";
import { clearAuthorization } from "../../../utils/session/clearAuthorization";
import { isIdentifier, newAuthRequest, validateCredentials } from "../../../api/newAuthorization";
import { isActiveToken, tokenExpiresAt } from "../../../utils/session/authorizationToken";

const REFRESH_BEFORE_EXPIRY = 120_000;
const REFRESH_RETRY_INTERVAL = 30_000;
const REDIRECT_BEFORE_EXPIRY = 30_000;

export function createTokenRefreshController(epoch: number, navigate: (path: string, options: NavigateOptions) => void) {
  const abortController = new AbortController();
  let isRefreshing = false;
  let lastRefreshAttemptAt = -Infinity;
  let expiryTimeout: ReturnType<typeof setTimeout> | undefined;
  let refreshTimeout: ReturnType<typeof setTimeout> | undefined;

  const remainingTokenLifetime = (token: string) => (tokenExpiresAt(token) ?? 0) - Date.now();

  function isCurrentSession(token: string) {
    const auth = useAuthorizationStore.getState();
    return !abortController.signal.aborted && auth.version === "keycloak" && auth.epoch === epoch && auth.token === token;
  }

  function reauthenticate() {
    const deviceId = useAuthorizationStore.getState().deviceId;
    clearAuthorization("keycloak", true);
    navigate(deviceId ? "/login/pin" : "/login", { replace: true });
  }

  async function refreshToken(token: string, storedRefreshToken: string | undefined) {
    lastRefreshAttemptAt = Date.now();
    isRefreshing = true;
    const isCurrent = () => isCurrentSession(token) && useAuthorizationStore.getState().refreshToken === storedRefreshToken;

    try {
      if (!isIdentifier(storedRefreshToken)) {
        throw new Error("Missing refreshToken");
      }

      const data = await newAuthRequest("/token/refresh", { refreshToken: storedRefreshToken }, "POST", token, abortController.signal);
      const credentials = validateCredentials(data, false);

      if (!isCurrent()) {
        return;
      }
      if (!isActiveToken(token)) {
        reauthenticate();
        return;
      }

      useAuthorizationStore.setState({ token: credentials.token, refreshToken: credentials.refreshToken });
    } catch (_error) {
      if (isCurrent() && remainingTokenLifetime(token) <= REDIRECT_BEFORE_EXPIRY) {
        reauthenticate();
      }
    } finally {
      isRefreshing = false;
      scheduleRefresh();
    }
  }

  function runRefreshCheck() {
    const { token, refreshToken: storedRefreshToken } = useAuthorizationStore.getState();
    if (!token || !isCurrentSession(token)) {
      return;
    }

    const remaining = remainingTokenLifetime(token);
    if (remaining <= 0) {
      reauthenticate();
      return;
    }

    if (isRefreshing) {
      return;
    }

    if (remaining > REFRESH_BEFORE_EXPIRY || Date.now() - lastRefreshAttemptAt < REFRESH_RETRY_INTERVAL) {
      scheduleRefresh();
      return;
    }

    clearTimeout(refreshTimeout);
    refreshTimeout = undefined;
    void refreshToken(token, storedRefreshToken);
  }

  function scheduleRefresh() {
    const { token } = useAuthorizationStore.getState();
    if (!token || !isCurrentSession(token) || isRefreshing || refreshTimeout !== undefined) {
      return;
    }

    // The same deadline handles the refresh window and the minimum time between attempts.
    const refreshAt = (tokenExpiresAt(token) ?? 0) - REFRESH_BEFORE_EXPIRY;
    const retryAt = lastRefreshAttemptAt + REFRESH_RETRY_INTERVAL;
    const delay = Math.max(0, Math.ceil(Math.max(refreshAt, retryAt) - Date.now()));
    refreshTimeout = setTimeout(() => {
      refreshTimeout = undefined;
      runRefreshCheck();
    }, delay);
  }

  function scheduleExpiry() {
    clearTimeout(expiryTimeout);

    const { token } = useAuthorizationStore.getState();
    if (!token || !isCurrentSession(token)) {
      return;
    }

    // Expiry is independent of the refresh deadline and any pending request.
    expiryTimeout = setTimeout(runRefreshCheck, Math.max(0, Math.ceil(remainingTokenLifetime(token))));
  }

  const unsubscribe = useAuthorizationStore.subscribe((auth, previous) => {
    if (auth.epoch !== epoch || auth.version !== "keycloak") {
      stop();
      return;
    }

    if (auth.token !== previous.token) {
      clearTimeout(refreshTimeout);
      refreshTimeout = undefined;
      scheduleExpiry();
      scheduleRefresh();
    }
  });
  scheduleExpiry();
  scheduleRefresh();

  function stop() {
    if (abortController.signal.aborted) {
      return;
    }

    abortController.abort();
    clearTimeout(expiryTimeout);
    clearTimeout(refreshTimeout);
    unsubscribe();
  }

  return { runRefreshCheck, stop };
}
